import { PHOENIX_TIMEZONE, PHOENIX_VENUE_ID } from "./config";

export const DAY_OPTIONS = [
  ["monday", "Monday", 1],
  ["tuesday", "Tuesday", 2],
  ["wednesday", "Wednesday", 3],
  ["thursday", "Thursday", 4],
  ["friday", "Friday", 5],
  ["saturday", "Saturday", 6],
  ["sunday", "Sunday", 0],
];

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const initialBookingForm = {
  contact: {
    fullName: "",
    organisationName: "",
    email: "",
    telephone: "",
    preferredContactMethod: "email",
    postcode: "",
  },
  bookingType: "one_off",
  selectedSpaceIds: [],
  oneOff: {
    date: "",
    startTime: "",
    endTime: "",
    alternativeDate: "",
    alternativeStartTime: "",
    alternativeEndTime: "",
  },
  recurrence: {
    startDate: "",
    endDate: "",
    daysOfWeek: [],
    startTime: "",
    endTime: "",
    frequency: "weekly",
    expectedSessions: "",
    excludedDates: "",
    customPattern: "",
  },
  multiDayDates: [{ date: "", startTime: "", endTime: "" }],
  activity: {
    name: "",
    description: "",
    expectedAttendees: "",
    attendeeAgeRange: "",
    childrenOrVulnerableAdults: "",
    attendanceType: "public",
    admissionFeeCharged: "",
    commercialActivity: "",
    foodServed: "",
    amplifiedSound: "",
    specialistEquipment: "",
    setupMinutes: "0",
    packDownMinutes: "0",
    accessibilityRequirements: "",
    additionalInformation: "",
  },
  compliance: {
    publicLiabilityInsurance: "",
    safeguardingRequired: "",
    safeguardingPolicy: "",
    dbsRequired: "",
    licenceRequired: "",
    storageRequired: "",
    uunStaffRequired: "",
  },
  declarations: {
    accurate: false,
    requestOnly: false,
    contactConsent: false,
    privacyAccepted: false,
    termsAccepted: false,
  },
  honeypot: "",
};

export function getLondonTodayISO(now = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: PHOENIX_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

export function timeToMinutes(value = "") {
  const [hours, minutes] = value.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

export function isValidTimeRange(startTime, endTime) {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  return start !== null && end !== null && end > start;
}

export function londonLocalToUtcIso(date, time) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || "") || !/^\d{2}:\d{2}$/.test(time || "")) {
    return "";
  }
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);
  const initial = new Date(Date.UTC(year, month - 1, day, hour, minute));
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: PHOENIX_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(initial)
    .reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  const representedAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute)
  );
  return new Date(initial.getTime() - (representedAsUtc - initial.getTime())).toISOString();
}

function requestedDate(date, startTime, endTime) {
  return {
    date,
    startTime,
    endTime,
    timezone: PHOENIX_TIMEZONE,
    startDateTime: londonLocalToUtcIso(date, startTime),
    endDateTime: londonLocalToUtcIso(date, endTime),
  };
}

function parseExcludedDates(value) {
  return String(value || "")
    .split(/[\s,]+/)
    .map((date) => date.trim())
    .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date));
}

export function buildRequestedDates(form, limit = 104) {
  if (form.bookingType === "general_enquiry") return [];
  if (form.bookingType === "one_off") {
    const { date, startTime, endTime } = form.oneOff;
    return date && startTime && endTime ? [requestedDate(date, startTime, endTime)] : [];
  }
  if (form.bookingType === "multi_day") {
    return form.multiDayDates
      .filter(({ date, startTime, endTime }) => date && startTime && endTime)
      .slice(0, limit)
      .map(({ date, startTime, endTime }) => requestedDate(date, startTime, endTime));
  }

  const recurrence = form.recurrence;
  const start = new Date(`${recurrence.startDate}T12:00:00Z`);
  const end = new Date(`${recurrence.endDate}T12:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];
  const selectedDays = new Set(
    DAY_OPTIONS.filter(([key]) => recurrence.daysOfWeek.includes(key)).map(([, , day]) => day)
  );
  const excluded = new Set(parseExcludedDates(recurrence.excludedDates));
  const expected = Math.max(1, Math.min(limit, Number(recurrence.expectedSessions) || limit));
  const dates = [];
  for (let cursor = new Date(start), week = 0; cursor <= end && dates.length < expected; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
    const date = cursor.toISOString().slice(0, 10);
    const daysFromStart = Math.floor((cursor.getTime() - start.getTime()) / 86400000);
    week = Math.floor(daysFromStart / 7);
    const correctWeek = recurrence.frequency !== "fortnightly" || week % 2 === 0;
    const correctMonth =
      recurrence.frequency !== "monthly" || cursor.getUTCDate() === start.getUTCDate();
    const chosenDay = selectedDays.size === 0 || selectedDays.has(cursor.getUTCDay());
    if (correctWeek && correctMonth && chosenDay && !excluded.has(date)) {
      dates.push(requestedDate(date, recurrence.startTime, recurrence.endTime));
    }
  }
  return dates;
}

function requireValue(errors, key, value, message) {
  if (value === "" || value === null || value === undefined || value === false) {
    errors[key] = message;
  }
}

export function needsSafeguardingDetails(form) {
  return (
    form?.activity?.childrenOrVulnerableAdults === "yes" ||
    form?.compliance?.safeguardingRequired === "yes"
  );
}

export function validateBookingStep(step, form, today = getLondonTodayISO()) {
  const errors = {};
  if (step === 1) {
    requireValue(errors, "fullName", form.contact.fullName.trim(), "Enter your full name.");
    if (!emailPattern.test(form.contact.email.trim())) errors.email = "Enter a valid email address.";
    requireValue(errors, "telephone", form.contact.telephone.trim(), "Enter a telephone number.");
    requireValue(
      errors,
      "preferredContactMethod",
      form.contact.preferredContactMethod,
      "Choose a preferred contact method."
    );
  }
  if (step === 2) requireValue(errors, "bookingType", form.bookingType, "Choose a booking type.");
  if (step === 3 && form.selectedSpaceIds.length === 0) {
    errors.selectedSpaceIds = "Choose at least one space or select ‘Unsure — please advise’.";
  }
  if (step === 4 && form.bookingType === "one_off") {
    const { date, startTime, endTime, alternativeDate, alternativeStartTime, alternativeEndTime } = form.oneOff;
    requireValue(errors, "date", date, "Choose a requested date.");
    if (date && date < today) errors.date = "Choose today or a future date.";
    if (!isValidTimeRange(startTime, endTime)) errors.time = "End time must be later than start time.";
    if (alternativeDate) {
      if (alternativeDate < today) errors.alternativeDate = "Choose today or a future date.";
      if (!isValidTimeRange(alternativeStartTime, alternativeEndTime)) {
        errors.alternativeTime = "Enter a valid alternative start and end time.";
      }
    }
  }
  if (step === 4 && form.bookingType === "recurring") {
    const r = form.recurrence;
    if (!r.startDate || r.startDate < today) errors.startDate = "Choose today or a future date.";
    if (!r.endDate || r.endDate < r.startDate) errors.endDate = "End date must be on or after the start date.";
    if (r.daysOfWeek.length === 0) errors.daysOfWeek = "Choose at least one day of the week.";
    if (!isValidTimeRange(r.startTime, r.endTime)) errors.recurrenceTime = "End time must be later than start time.";
    if (!(Number(r.expectedSessions) > 0)) errors.expectedSessions = "Enter the expected number of sessions.";
    if (r.frequency === "custom" && !r.customPattern.trim()) errors.customPattern = "Describe the custom pattern.";
  }
  if (step === 4 && form.bookingType === "multi_day") {
    if (form.multiDayDates.length === 0) errors.multiDayDates = "Add at least one date.";
    form.multiDayDates.forEach((item, index) => {
      if (!item.date || item.date < today) errors[`multiDay-${index}`] = `Date ${index + 1} must be today or later.`;
      if (!isValidTimeRange(item.startTime, item.endTime)) errors[`multiDayTime-${index}`] = `Date ${index + 1} needs a valid time range.`;
    });
  }
  if (step === 5) {
    requireValue(errors, "activityName", form.activity.name.trim(), "Enter the activity or event name.");
    requireValue(errors, "activityDescription", form.activity.description.trim(), "Describe the proposed activity.");
    if (!(Number(form.activity.expectedAttendees) > 0)) errors.expectedAttendees = "Enter the expected number of attendees.";
    requireValue(errors, "attendeeAgeRange", form.activity.attendeeAgeRange.trim(), "Enter the attendees’ age range.");
    [
      "childrenOrVulnerableAdults",
      "admissionFeeCharged",
      "commercialActivity",
      "foodServed",
      "amplifiedSound",
      "specialistEquipment",
    ].forEach((key) => requireValue(errors, key, form.activity[key], "Choose an answer."));
  }
  if (step === 6) {
    [
      "publicLiabilityInsurance",
      "safeguardingRequired",
      "licenceRequired",
      "storageRequired",
      "uunStaffRequired",
    ].forEach((key) => requireValue(errors, key, form.compliance[key], "Choose an answer."));
    if (needsSafeguardingDetails(form)) {
      ["safeguardingPolicy", "dbsRequired"].forEach((key) =>
        requireValue(errors, key, form.compliance[key], "Choose an answer.")
      );
    }
  }
  if (step === 7) {
    Object.entries(form.declarations).forEach(([key, checked]) =>
      requireValue(errors, key, checked, "This confirmation is required.")
    );
  }
  return errors;
}

export function validateCompleteBooking(form, today = getLondonTodayISO()) {
  return [1, 2, 3, 4, 5, 6, 7].reduce(
    (all, step) => ({ ...all, ...validateBookingStep(step, form, today) }),
    {}
  );
}

export function createBookingReference(now = new Date(), random = Math.random) {
  const date = new Intl.DateTimeFormat("en-CA", {
    timeZone: PHOENIX_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .format(now)
    .replaceAll("-", "");
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let suffix = "";
  for (let index = 0; index < 5; index += 1) {
    suffix += alphabet[Math.floor(random() * alphabet.length) % alphabet.length];
  }
  return `PHX-${date}-${suffix}`;
}

export function calculateIndicativeEstimate(form, spaces) {
  const selected = spaces.filter((space) => form.selectedSpaceIds.includes(space.id));
  if (selected.length === 0 || selected.some((space) => !Number.isFinite(Number(space.hourlyRate)))) {
    return { available: false, rate: null, hours: 0, sessions: 0, subtotal: null };
  }
  const dates = buildRequestedDates(form);
  const hours = dates.reduce((sum, item) => {
    return sum + Math.max(0, (timeToMinutes(item.endTime) - timeToMinutes(item.startTime)) / 60);
  }, 0);
  const rate = selected.reduce((sum, space) => sum + Number(space.hourlyRate), 0);
  return {
    available: hours > 0,
    rate,
    hours,
    sessions: dates.length,
    subtotal: Math.round(rate * hours * 100) / 100,
  };
}

export function transformBookingForm(form, spaces = []) {
  const requestedDates = buildRequestedDates(form);
  const estimate = calculateIndicativeEstimate(form, spaces);
  const safeguardingDetailsRequired = needsSafeguardingDetails(form);
  return {
    venueId: PHOENIX_VENUE_ID,
    bookingType: form.bookingType,
    selectedSpaceIds: [...form.selectedSpaceIds],
    contact: {
      fullName: form.contact.fullName.trim(),
      email: form.contact.email.trim().toLowerCase(),
      telephone: form.contact.telephone.trim(),
      preferredContactMethod: form.contact.preferredContactMethod,
      postcode: form.contact.postcode.trim(),
    },
    organisation: { name: form.contact.organisationName.trim() },
    activityDetails: {
      name: form.activity.name.trim(),
      description: form.activity.description.trim(),
      attendanceType: form.activity.attendanceType,
      admissionFeeCharged: form.activity.admissionFeeCharged === "yes",
      commercialActivity: form.activity.commercialActivity === "yes",
      foodServed: form.activity.foodServed === "yes",
      amplifiedSound: form.activity.amplifiedSound === "yes",
      specialistEquipment: form.activity.specialistEquipment === "yes",
      setupMinutes: Number(form.activity.setupMinutes) || 0,
      packDownMinutes: Number(form.activity.packDownMinutes) || 0,
      accessibilityRequirements: form.activity.accessibilityRequirements.trim(),
      additionalInformation: form.activity.additionalInformation.trim(),
    },
    attendance: {
      expectedCount: Number(form.activity.expectedAttendees) || 0,
      ageRange: form.activity.attendeeAgeRange.trim(),
      childrenOrVulnerableAdults: form.activity.childrenOrVulnerableAdults === "yes",
    },
    requestedDates,
    alternativeRequest:
      form.bookingType === "one_off" && form.oneOff.alternativeDate
        ? requestedDate(
            form.oneOff.alternativeDate,
            form.oneOff.alternativeStartTime,
            form.oneOff.alternativeEndTime
          )
        : null,
    recurrence:
      form.bookingType === "recurring"
        ? {
            ...form.recurrence,
            expectedSessions: Number(form.recurrence.expectedSessions) || 0,
            excludedDates: parseExcludedDates(form.recurrence.excludedDates),
          }
        : null,
    safeguarding: {
      childrenOrVulnerableAdults: form.activity.childrenOrVulnerableAdults === "yes",
      proceduresRequired: form.compliance.safeguardingRequired === "yes",
      policyHeld: safeguardingDetailsRequired && form.compliance.safeguardingPolicy === "yes",
      dbsRequired: safeguardingDetailsRequired && form.compliance.dbsRequired === "yes",
    },
    insurance: { publicLiabilityHeld: form.compliance.publicLiabilityInsurance === "yes" },
    compliance: {
      licenceRequired: form.compliance.licenceRequired === "yes",
      storageRequired: form.compliance.storageRequired === "yes",
      uunStaffRequired: form.compliance.uunStaffRequired === "yes",
    },
    declarations: { ...form.declarations },
    indicativeEstimate: estimate,
    honeypot: form.honeypot,
    timezone: PHOENIX_TIMEZONE,
  };
}

function asMillis(value) {
  if (!value) return NaN;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  return new Date(value).getTime();
}

export function periodsOverlap(first, second) {
  const firstStart = asMillis(first.startDateTime);
  const firstEnd = asMillis(first.endDateTime);
  const secondStart = asMillis(second.startDateTime);
  const secondEnd = asMillis(second.endDateTime);
  return [firstStart, firstEnd, secondStart, secondEnd].every(Number.isFinite)
    ? firstStart < secondEnd && secondStart < firstEnd
    : false;
}

export function spacesOverlap(firstSpaceIds = [], secondSpaceIds = []) {
  return (
    firstSpaceIds.includes("whole-centre") ||
    secondSpaceIds.includes("whole-centre") ||
    firstSpaceIds.some((id) => secondSpaceIds.includes(id))
  );
}

export function blockOccursOnDate(block, date) {
  if (!block?.date || !date) return false;
  if (block.date === date) return true;
  const recurrence = block.recurrence;
  if (!recurrence || date < block.date || !recurrence.endDate || date > recurrence.endDate) return false;
  const start = new Date(`${block.date}T12:00:00Z`);
  const candidate = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(candidate.getTime())) return false;
  const daysApart = Math.floor((candidate.getTime() - start.getTime()) / 86400000);
  const dayKey = DAY_OPTIONS.find(([, , day]) => day === candidate.getUTCDay())?.[0];
  const selectedDays = recurrence.daysOfWeek || [];
  const dayMatches = selectedDays.length ? selectedDays.includes(dayKey) : candidate.getUTCDay() === start.getUTCDay();
  if (!dayMatches) return false;
  if (recurrence.frequency === "weekly") return true;
  if (recurrence.frequency === "fortnightly") return Math.floor(daysApart / 7) % 2 === 0;
  if (recurrence.frequency === "monthly") return candidate.getUTCDate() === start.getUTCDate();
  return false;
}

function recurringBlockOverlapsDate(block, requestedDate) {
  if (!blockOccursOnDate(block, requestedDate.date)) return false;
  const requestedStart = timeToMinutes(requestedDate.startTime);
  const requestedEnd = timeToMinutes(requestedDate.endTime);
  const blockStart = timeToMinutes(block.startTime);
  const blockEnd = timeToMinutes(block.endTime);
  return [requestedStart, requestedEnd, blockStart, blockEnd].every(Number.isFinite)
    ? requestedStart < blockEnd && blockStart < requestedEnd
    : false;
}

export function findBookingConflicts(candidate, bookings = [], blockedPeriods = []) {
  const activeStatuses = new Set([
    "provisional_hold",
    "approved_awaiting_payment",
    "confirmed",
  ]);
  const dates = candidate.requestedDates || [];
  const bookingConflicts = bookings.filter(
    (booking) =>
      booking.id !== candidate.id &&
      activeStatuses.has(booking.status) &&
      spacesOverlap(candidate.selectedSpaceIds, booking.selectedSpaceIds) &&
      dates.some((date) => (booking.requestedDates || []).some((other) => periodsOverlap(date, other)))
  );
  const blockConflicts = blockedPeriods.filter(
    (block) =>
      spacesOverlap(candidate.selectedSpaceIds, block.spaceIds) &&
      dates.some((date) => periodsOverlap(date, block) || recurringBlockOverlapsDate(block, date))
  );
  return { bookingConflicts, blockConflicts, hasConflict: bookingConflicts.length + blockConflicts.length > 0 };
}

export function calculateAdminPricing(pricing = {}) {
  const hourlyRate = Number(pricing.hourlyRate) || 0;
  const chargeableHours = Number(pricing.chargeableHours) || 0;
  const base = hourlyRate * chargeableHours;
  const additions = [
    "cleaningCharge",
    "staffingCharge",
    "equipmentCharge",
    "otherCharges",
  ].reduce((sum, key) => sum + (Number(pricing[key]) || 0), 0);
  const discount = Number(pricing.discount) || 0;
  const total = Math.max(0, base + additions - discount);
  const amountPaid = Number(pricing.amountPaid) || 0;
  return {
    ...pricing,
    subtotal: Math.round(base * 100) / 100,
    additionalCharges: Math.round(additions * 100) / 100,
    totalAmount: Math.round(total * 100) / 100,
    balanceDue: Math.max(0, Math.round((total - amountPaid) * 100) / 100),
  };
}

export function isActiveAdminRecord(record) {
  return Boolean(record && record.active === true);
}

const ALLOWED_TRANSITIONS = {
  submitted: ["under_review", "more_information_required", "provisional_hold", "rejected", "cancelled"],
  under_review: ["more_information_required", "provisional_hold", "approved_awaiting_payment", "rejected", "cancelled"],
  more_information_required: ["under_review", "provisional_hold", "rejected", "cancelled"],
  provisional_hold: ["under_review", "approved_awaiting_payment", "confirmed", "rejected", "cancelled"],
  approved_awaiting_payment: ["confirmed", "under_review", "cancelled"],
  confirmed: ["completed", "cancelled"],
  rejected: ["under_review"],
  cancelled: ["under_review"],
  completed: [],
};

export function canChangeBookingStatus(previousStatus, nextStatus) {
  return previousStatus === nextStatus || (ALLOWED_TRANSITIONS[previousStatus] || []).includes(nextStatus);
}
